import type { DateTime, Duration } from "luxon"

import { toCurrency, toMonetaryAmount, zeroDinero } from "../../../../../modules/currency/currencyLib"
import type { MonetaryAmount } from "../../../../../modules/currency/currencyTypes"
import type { DateTimeFactory } from "../../../../../modules/localization/useDateTime"
import type { PartialOrderLine } from "../../../../../modules/merchant/merchant/production/meetingService/meetingServiceApi"
import { PartialOrderLineType } from "../../../../../modules/merchant/merchant/production/meetingService/meetingServiceTypes"
import { isProductWishOrderLine, type DeliveryTimesField, type DeliveryWithCart } from "../form/formTypes"
import { sortedDeliveryTimes, earliestDeliveryTime } from "../shop/delivery/helpers"

// Mirrors the Outlook add-in's tolerance: the two sides store minutes, so sub-minute drift is noise.
const MISMATCH_TOLERANCE_MINUTES = 1

export type BookingMove = Readonly<{
    previousStart: DateTime
    newStart: DateTime
    newEnd: DateTime
}>

function toDeliveryDateTime(
    deliveries: DeliveryTimesField,
    time: string | null,
    dateTimeFactory: DateTimeFactory,
): DateTime | null {
    if (!deliveries.deliveryDate || !time) return null

    const parsed = dateTimeFactory.fromISO(`${deliveries.deliveryDate}T${time}`)
    return parsed.isValid ? parsed : null
}

/**
 * The delivery time is allowed to sit outside the meeting window — catering can legitimately be
 * delivered before or after — so only a moved *day* or a delivery that fell out of the new window
 * is worth interrupting the user for. Same rule as the add-in's `isOutsideMeetingWindow`.
 */
export function hasDeliveryMismatch(
    deliveries: DeliveryTimesField,
    move: BookingMove,
    dateTimeFactory: DateTimeFactory,
): boolean {
    const deliveryDateTimes = deliveries.deliveryTimes
        .map((delivery) => toDeliveryDateTime(deliveries, delivery.time, dateTimeFactory))
        .filter((deliveryDateTime): deliveryDateTime is DateTime => deliveryDateTime !== null)

    if (!deliveryDateTimes.length) return false

    return deliveryDateTimes.some((deliveryDateTime) => {
        if (!deliveryDateTime.hasSame(move.newStart, "day")) return true

        return (
            deliveryDateTime.diff(move.newStart, "minutes").minutes < -MISMATCH_TOLERANCE_MINUTES ||
            deliveryDateTime.diff(move.newEnd, "minutes").minutes > MISMATCH_TOLERANCE_MINUTES
        )
    })
}

/**
 * Every delivery keeps its offset from the meeting start, so "same hour, next day" stays that way
 * and "30 min before start" follows the booking. `DeliveryTimesField` carries one date for all
 * times, so the whole set necessarily moves by the same delta.
 */
export function shiftDeliveries(
    deliveries: DeliveryTimesField,
    delta: Duration,
    dateTimeFactory: DateTimeFactory,
): DeliveryTimesField {
    // Date and time must come off the SAME shifted moment. Shifting them separately — midnight plus
    // delta for the date, date-and-time plus delta for the time — moves the date a day back on any
    // negative delta, because midnight crosses the day boundary while the time of day does not.
    const shifted = deliveries.deliveryTimes.map((delivery) => ({
        delivery,
        shiftedAt: toDeliveryDateTime(deliveries, delivery.time, dateTimeFactory)?.plus(delta) ?? null,
    }))

    const shiftedTimes: DeliveryWithCart[] = shifted.map(({ delivery, shiftedAt }) =>
        shiftedAt ? { ...delivery, time: dateTimeFactory.toHourMinute(shiftedAt) } : delivery,
    )
    const sorted = sortedDeliveryTimes(shiftedTimes)

    // The field carries one date for all of its times, so the earliest delivery anchors it.
    const [anchor] = shifted
        .map(({ shiftedAt }) => shiftedAt)
        .filter((shiftedAt): shiftedAt is DateTime => shiftedAt !== null)
        .sort((a, b) => a.toMillis() - b.toMillis())

    return {
        deliveryDate: anchor ? dateTimeFactory.toDate(anchor) : deliveries.deliveryDate,
        deliveryTimes: sorted,
        activeTime: earliestDeliveryTime(sorted),
    }
}

export type PartialDeliveryPayload = Readonly<{
    deliveryTime: string
    seats: number
    orderLines: ReadonlyArray<PartialOrderLine>
}>

function toPartialOrderLines(delivery: DeliveryWithCart, zeroPrice: MonetaryAmount): PartialOrderLine[] {
    return delivery.orderLines
        .filter((orderLine) => (orderLine.quantity ?? 0) !== 0)
        .map((orderLine): PartialOrderLine => {
            if (isProductWishOrderLine(orderLine)) {
                return {
                    type: PartialOrderLineType.PRODUCT_WISH,
                    productName: orderLine.name,
                    items: orderLine.quantity,
                    price: orderLine.price ?? zeroPrice,
                }
            }

            if (orderLine.isExtraService) {
                return {
                    type: PartialOrderLineType.EXTRA_SERVICE,
                    productId: orderLine.id,
                    items: orderLine.quantity,
                }
            }

            return { productId: orderLine.id, items: orderLine.quantity }
        })
}

/**
 * The endpoint replaces the whole delivery set, so every slot is sent back with its lines — only
 * the times differ from what is already saved.
 */
export function toPartialDeliveries(
    deliveries: DeliveryTimesField,
    seats: number,
    currency: MonetaryAmount["currency"],
    dateTimeFactory: DateTimeFactory,
): PartialDeliveryPayload[] {
    // Through the money helpers so the scale follows the currency's own exponent — a hand-built
    // `scale: 2` is wrong for the currencies that do not have two decimals.
    const zeroPrice = toMonetaryAmount(zeroDinero(toCurrency(currency)))

    return deliveries.deliveryTimes.reduce<PartialDeliveryPayload[]>((payload, delivery) => {
        const deliveryDateTime = toDeliveryDateTime(deliveries, delivery.time, dateTimeFactory)
        if (!deliveryDateTime) return payload

        return [
            ...payload,
            {
                deliveryTime: dateTimeFactory.toApi(deliveryDateTime),
                seats,
                orderLines: toPartialOrderLines(delivery, zeroPrice),
            },
        ]
    }, [])
}

/**
 * Localized, so the delivery reads in the same clock as the booking line next to it — the stored
 * "HH:mm" would show 24-hour times beside a 12-hour booking in en-US.
 */
export function formatDeliverySummary(deliveries: DeliveryTimesField, dateTimeFactory: DateTimeFactory): string {
    return sortedDeliveryTimes(deliveries.deliveryTimes)
        .map((delivery) => (delivery.time ? dateTimeFactory.fromHourMinute(delivery.time) : null))
        .filter((time): time is DateTime => time !== null && time.isValid)
        .map((time) => dateTimeFactory.formatTime(time))
        .join(", ")
}
